import { alpha, createTheme } from "@mui/material/styles";
import { colors } from "healthex-common/colors";
import { fontWeights } from "healthex-common/weights";
import { fontSizes } from "healthex-common/sizes";
export function createMuiTheme(appFonts) {
    const theme = createTheme({
        typography: {
            fontFamily: appFonts.join(","),
            button: {
                textTransform: "none",
            },
        },
        spacing: (factor) => `${2 * factor}px`,
        palette: {
            primary: {
                main: colors.navy_main,
                contrastText: colors.white_main,
            },
            error: {
                main: colors.tulip_main,
            },
            success: {
                main: colors.forest_main,
            },
        },
        components: {
            MuiButtonBase: {
                defaultProps: {
                    disableRipple: true,
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        fontFamily: appFonts.join(","),
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    root: {
                        ".MuiAutocomplete-endAdornment": {
                            top: "calc(50% - 16px)",
                        },
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        fontSize: "16px",
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontSize: "16px",
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: colors.navy_8,
                        },
                        fontSize: "16px",
                        fontWeight: fontWeights.SEMI_BOLD,
                        color: colors.black_main,
                    },
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        fontSize: "16px",
                        color: colors.black_main,
                    },
                    columnHeader: {
                        fontWeight: fontWeights.SEMI_BOLD,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    arrow: {
                        color: colors.navy_main,
                    },
                    tooltip: {
                        fontSize: fontSizes.TINY,
                        fontWeight: fontWeights.NORMAL,
                        color: colors.white_main,
                        backgroundColor: colors.navy_main,
                        letterSpacing: "0.4px",
                        lineHeight: "normal",
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    label: {
                        color: colors.black_main,
                        fontSize: fontSizes.STANDARD,
                        lineHeight: "normal",
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    asterisk: {
                        color: colors.tulip_main,
                    },
                    root: {
                        "&.Mui-disabled .MuiInputLabel-asterisk": {
                            color: "rgba(0,0,0,0.38)",
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiInputAdornment-positionEnd p": {
                            fontSize: "4px",
                        },
                        "& .MuiInputBase-input": {
                            color: colors.black_main,
                            fontSize: "16px",
                            fontStyle: "normal",
                            fontWeight: fontWeights.NORMAL,
                            lineHeight: "normal",
                            letterSpacing: "0.15px",
                        },
                        "& .MuiInputBase-root": {
                            backgroundColor: colors.white_main,
                            fieldset: {
                                borderColor: colors.black_30,
                            },
                            "&.Mui-error fieldset": {
                                borderColor: colors.tulip_main,
                            },
                            "&:hover fieldset": {
                                borderColor: colors.black_main,
                            },
                        },
                        "& .MuiInputBase-root.Mui-focused fieldset": {
                            borderColor: `${colors.navy_main}`,
                            borderWidth: "2px",
                            top: "-4px",
                        },
                        "& .MuiInputBase-input.Mui-disabled": {
                            "-webkit-text-fill-color": "unset",
                            color: colors.black_30,
                        },
                        "& .MuiInputBase-root.Mui-disabled": {
                            backgroundColor: colors.black_4,
                            fieldset: {
                                borderColor: colors.black_30,
                            },
                        },
                        "& .MuiInputBase-input:placeholder-shown": {
                            color: colors.black_30,
                        },
                        "& .MuiInputLabel-formControl:not(.Mui-focused)": {
                            color: colors.black_30,
                        },
                    },
                },
            },
        },
    });
    theme.components.MuiButton = {
        styleOverrides: {
            root: {
                fontSize: "16px",
                borderRadius: "999px",
            },
            contained: {
                ":active": {
                    backgroundColor: colors.navy_main,
                },
                "@media (hover: hover)": {
                    ":hover": {
                        boxShadow: "none",
                        backgroundColor: colors.navy_dark,
                    },
                },
                ":focus-visible": {
                    backgroundColor: colors.navy_dark,
                },
                "&.MuiButton-containedError": {
                    ":active": {
                        backgroundColor: colors.tulip_main,
                    },
                    "@media (hover: hover)": {
                        ":hover": {
                            boxShadow: "none",
                            backgroundColor: colors.tulip_dark,
                        },
                    },
                    ":focus-visible": {
                        backgroundColor: colors.tulip_dark,
                    },
                },
                boxShadow: "none",
            },
            outlined: {
                ":focus-visible": {
                    backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
                    border: `1px solid ${theme.palette.primary.main}`,
                },
                backgroundColor: "white",
                "&.MuiButton-outlinedError": {
                    borderColor: colors.tulip_50,
                },
            },
        },
    };
    return theme;
}
